import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { HlmDropdownMenuImports } from '@lite-workspace/shared/helm-ui/dropdown-menu';
import { HlmLogo } from '@lite-workspace/shared/helm-ui/logo';
import { NotificationsBellComponent } from '@lite-workspace/shared/blocks/notifications';
import { ToastHostComponent } from '@lite-workspace/shared/helm-ui/toast';
import { bffLogout, clearAuth, isBffMode, redirectToBffLogin, redirectToIdentity } from '@lite-workspace/shared/session';
import { ThemeService } from '../core/theme.service';

interface NavItem {
  label: string;
  link: string;
}

@Component({
  selector: 'app-shell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    HlmDropdownMenuImports,
    HlmLogo,
    NotificationsBellComponent,
    ToastHostComponent,
  ],
  template: `
    <div class="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <header class="topbar sticky top-0 z-20 flex h-11 items-center justify-between px-3.5 md:px-5">
        <div class="flex h-full min-w-0 items-center">
          <!-- Same box as the product wordmark on the other shells; the admin has no product menu, so it stays a link. -->
          <a
            routerLink="/users-list"
            aria-label="LUMIR ADMIN"
            class="inline-flex h-7.5 cursor-pointer items-center border border-transparent px-2"
          >
            <hlm-logo word="ADMIN" />
          </a>
          <button
            [hlmDropdownMenuTrigger]="navMenu"
            align="start"
            side="bottom"
            aria-label="Навигация"
            class="ml-2 grid size-8 shrink-0 place-items-center rounded-lg text-c2 hover:bg-panel hover:text-ink lg:hidden"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              aria-hidden="true"
            >
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        <nav class="absolute left-1/2 hidden h-full -translate-x-1/2 items-center gap-0.5 lg:flex">
          @for (item of navItems; track item.label) {
            <a [routerLink]="item.link" routerLinkActive="tab-on" [routerLinkActiveOptions]="{ exact: false }" class="navtab">{{
              item.label
            }}</a>
          }
        </nav>

        <div class="flex h-full shrink-0 items-center gap-1.5">
          <button
            type="button"
            class="grid size-8 place-items-center rounded-md text-c2 hover:bg-panel hover:text-ink"
            [attr.aria-label]="isDark() ? 'Включить светлую тему' : 'Включить тёмную тему'"
            [attr.aria-pressed]="isDark()"
            (click)="theme.toggle()"
          >
            @if (isDark()) {
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="4" />
                <path
                  d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2
                     M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"
                />
              </svg>
            } @else {
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
              </svg>
            }
          </button>
          <app-notifications-bell />
          <button
            [hlmDropdownMenuTrigger]="accountMenu"
            align="end"
            side="bottom"
            aria-label="Аккаунт"
            class="grid size-8 place-items-center rounded-full bg-panel text-c2 hover:text-ink"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
            </svg>
          </button>
        </div>
      </header>

      <ng-template #navMenu>
        <div hlmDropdownMenu class="min-w-50">
          @for (item of navItems; track item.label) {
            <a hlmDropdownMenuItem [routerLink]="item.link" routerLinkActive="text-acc font-semibold">{{ item.label }}</a>
          }
        </div>
      </ng-template>

      <ng-template #accountMenu>
        <div hlmDropdownMenu class="min-w-40">
          <button hlmDropdownMenuItem (click)="logout()">Выйти</button>
        </div>
      </ng-template>

      <main class="flex min-h-0 flex-1 flex-col"><router-outlet /></main>
    </div>

    <!-- Единственная площадка тостов консоли: редактор шаблона держит свою, вне оболочки. -->
    <hlm-toast-host />
  `,
  styles: [
    `
      .topbar {
        background: color-mix(in oklch, var(--panel) 92%, transparent);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        border-bottom: 1px solid var(--line2);
      }
      .navtab {
        position: relative;
        display: flex;
        align-items: center;
        height: 100%;
        padding: 0 14px;
        font-size: 14.5px;
        font-weight: 600;
        color: var(--c3);
        white-space: nowrap;
        transition: color var(--t-fast) var(--ease-smooth);
      }
      .navtab:hover {
        color: var(--ink);
      }
      .navtab.tab-on {
        color: var(--acc-2);
      }
      .navtab.tab-on::after {
        content: '';
        position: absolute;
        left: 13px;
        right: 13px;
        bottom: 0;
        height: 2px;
        background: var(--acc);
        border-radius: 2px 2px 0 0;
      }
    `,
  ],
})
export class ShellComponent {
  protected readonly theme = inject(ThemeService);

  protected readonly navItems: NavItem[] = [
    { label: 'Пользователи', link: '/users-list' },
    { label: 'Запросы на шаблоны', link: '/users-requests' },
    { label: 'Шаблоны на проверке', link: '/partner-templates' },
    { label: 'Заявки на аренду', link: '/rental-requests' },
    { label: 'Журнал вебхуков', link: '/webhook-events' },
    { label: 'Шрифты', link: '/fonts' },
    { label: 'Типографии', link: '/print-services' },
  ];

  protected readonly isDark = computed(() => this.theme.resolvedTheme() === 'dark');

  protected logout(): void {
    if (isBffMode()) {
      void bffLogout().then(() => redirectToBffLogin('/'));
      return;
    }

    clearAuth();
    redirectToIdentity('/');
  }
}
